import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import API, { fmt } from '@/lib/api';
import { toast } from 'sonner';
import { FileText, Sparkle, ArrowsClockwise, LinkSimple, LinkBreak, CircleNotch } from '@phosphor-icons/react';

/**
 * Koppel een geüploade inkomende factuur aan een kost.
 * Leest (indien nodig met AI) leverancier, factuurnummer, factuurdatum en bedragen uit het document;
 * gebruiker kiest of het bedrag excl. of incl. BTW overgenomen wordt. Factuurdatum is altijd leidend.
 */
export const KostInkomendKoppel = ({ value, kostId, onOvernemen, onOntkoppel }) => {
  const [kandidaten, setKandidaten] = useState([]);
  const [zoek, setZoek] = useState('');
  const [gekozen, setGekozen] = useState(value || '');
  const [info, setInfo] = useState(null);
  const [basis, setBasis] = useState('excl');
  const [bezig, setBezig] = useState(false);

  useEffect(() => {
    API.get('/kosten/inkomende-kandidaten').then(r => setKandidaten(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  useEffect(() => { setGekozen(value || ''); if (!value) setInfo(null); }, [value]);

  const lees = async (did, force = false) => {
    if (!did) return;
    setBezig(true);
    try {
      const r = await API.post(`/kosten/lees-inkomend/${did}${force ? '?force=true' : ''}`);
      setInfo(r.data);
      setBasis(r.data?.excl != null ? 'excl' : 'incl');
      if (r.data?.ai_gebruikt) toast.success('Factuur gelezen met AI — controleer de gegevens');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Factuur lezen mislukt');
    } finally { setBezig(false); }
  };

  const lijst = useMemo(() => {
    const q = zoek.trim().toLowerCase();
    return kandidaten.filter(d => !d.kost_id || d.kost_id === kostId || d.id === value)
      .filter(d => !q || [d.leverancier, d.factuurnummer, d.filename, d.datum, d.betreft].some(v => (v || '').toLowerCase().includes(q)));
  }, [kandidaten, zoek, kostId, value]);

  const label = (d) => {
    const bedrag = d.totaal != null ? fmt(d.totaal) : (d.excl != null ? `${fmt(d.excl)} excl.` : '');
    const kop = d.leverancier ? `${d.datum || '?'} · ${d.leverancier}${d.factuurnummer ? ` · ${d.factuurnummer}` : ''}` : (d.filename || d.id);
    return `${kop}${bedrag ? ` · ${bedrag}` : ''}${d.status_doc === 'te_verwerken' ? ' · ⚠ nog niet gelezen' : ''}`;
  };

  const overnemen = () => {
    if (!info) return;
    onOvernemen({
      inkomend_doc_id: info.doc_id,
      datum: info.datum,
      leverancier: info.leverancier,
      referentie: info.factuurnummer,
      omschrijving: info.betreft,
      categorie: info.categorie_suggestie,
      categorie_bron: info.categorie_bron,
      categorie_geleerd_aantal: info.categorie_geleerd_aantal,
      btw_tarief: info.btw_tarief,
      bedrag_basis: basis,
      bedrag: basis === 'incl' ? info.incl : info.excl,
    });
    toast.success('Factuurgegevens overgenomen in de kost');
  };

  return (
    <div className="border border-[#002FA7]/25 bg-[#F4F7FF] p-3 space-y-2" data-testid="kost-inkomend-koppel">
      <div className="flex items-center justify-between gap-2">
        <label className="text-[11px] font-bold text-[#002FA7] uppercase inline-flex items-center gap-1">
          <FileText size={12} weight="bold" /> Koppel inkomende factuur
        </label>
        {value && (
          <button type="button" className="text-[11px] text-red-600 hover:underline inline-flex items-center gap-1" onClick={() => { setGekozen(''); setInfo(null); onOntkoppel(); }} data-testid="kost-inkomend-ontkoppel">
            <LinkBreak size={12} /> Ontkoppelen
          </button>
        )}
      </div>
      <div className="grid grid-cols-[1fr_2fr] gap-2">
        <input className="field-input text-xs" placeholder="Zoek leverancier / nr…" value={zoek} onChange={e => setZoek(e.target.value)} data-testid="kost-inkomend-zoek" />
        <select className="field-input text-xs" value={gekozen} onChange={e => { setGekozen(e.target.value); setInfo(null); if (e.target.value) lees(e.target.value); }} data-testid="kost-inkomend-select">
          <option value="">— Kies een geüploade factuur ({lijst.length}) —</option>
          {lijst.map(d => <option key={d.id} value={d.id}>{label(d)}</option>)}
        </select>
      </div>
      {value && !info && !bezig && (
        <div className="text-[11px] text-emerald-700 inline-flex items-center gap-1" data-testid="kost-inkomend-gekoppeld">
          <LinkSimple size={12} weight="bold" /> Gekoppeld aan factuur {value}
          <button type="button" className="ml-2 text-[#002FA7] hover:underline" onClick={() => lees(value)}>Gegevens tonen</button>
        </div>
      )}
      {bezig && (
        <div className="text-[11px] text-[#002FA7] inline-flex items-center gap-1" data-testid="kost-inkomend-bezig">
          <CircleNotch size={12} className="animate-spin" /> Factuur wordt gelezen…
        </div>
      )}
      {info && !bezig && (
        <div className="bg-white border border-gray-200 p-2 space-y-2" data-testid="kost-inkomend-info">
          <div className="grid grid-cols-3 gap-2 text-[11px]">
            <div><div className="text-gray-500 uppercase text-[9px] font-bold">Leverancier</div><div className="font-semibold truncate" data-testid="kost-inkomend-lev">{info.leverancier || '—'}</div></div>
            <div><div className="text-gray-500 uppercase text-[9px] font-bold">Factuurnr</div><div className="font-mono" data-testid="kost-inkomend-nr">{info.factuurnummer || '—'}</div></div>
            <div><div className="text-gray-500 uppercase text-[9px] font-bold">Factuurdatum</div><div className="font-mono" data-testid="kost-inkomend-datum">{info.datum || '—'}</div></div>
          </div>
          <div className="text-[10px] text-gray-600">Bedrag overnemen als:</div>
          <div className="grid grid-cols-2 gap-2">
            <label className={`flex items-center gap-2 p-2 border cursor-pointer ${basis === 'excl' ? 'border-[#002FA7] bg-blue-50' : 'border-gray-200'}`}>
              <input type="radio" name="kost-basis" checked={basis === 'excl'} onChange={() => setBasis('excl')} disabled={info.excl == null} data-testid="kost-inkomend-basis-excl" />
              <span className="text-xs"><strong>Excl. BTW</strong> — {info.excl != null ? fmt(info.excl) : '€ ?'}{info.btw_bedrag != null && <span className="text-gray-500"> (+ {fmt(info.btw_bedrag)} BTW · {info.btw_tarief}%)</span>}</span>
            </label>
            <label className={`flex items-center gap-2 p-2 border cursor-pointer ${basis === 'incl' ? 'border-[#002FA7] bg-blue-50' : 'border-gray-200'}`}>
              <input type="radio" name="kost-basis" checked={basis === 'incl'} onChange={() => setBasis('incl')} disabled={info.incl == null} data-testid="kost-inkomend-basis-incl" />
              <span className="text-xs"><strong>Incl. BTW</strong> — {info.incl != null ? fmt(info.incl) : '€ ?'}</span>
            </label>
          </div>
          <div className="flex items-center justify-between gap-2">
            <button type="button" className="text-[11px] text-gray-500 hover:text-[#002FA7] inline-flex items-center gap-1" onClick={() => lees(info.doc_id, true)} data-testid="kost-inkomend-herlees">
              <ArrowsClockwise size={12} /> Opnieuw lezen met AI
            </button>
            <button type="button" className="btn btn-primary btn-sm inline-flex items-center gap-1" onClick={overnemen} data-testid="kost-inkomend-overnemen">
              <Sparkle size={13} weight="fill" /> Overnemen in kost
            </button>
          </div>
        </div>
      )}
      {kandidaten.length === 0 && (
        <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 p-2 flex items-center justify-between gap-2" data-testid="kost-inkomend-leeg">
          <span>Nog geen geüploade facturen. Upload eerst je factuur (PDF) bij <strong>Inkomende facturen</strong>, daarna kan je ze hier kiezen.</span>
          <Link to="/inkomende-documenten" className="btn btn-sm bg-[#002FA7] text-white font-bold whitespace-nowrap" data-testid="kost-inkomend-naar-upload">Naar Inkomende facturen →</Link>
        </div>
      )}
      <div className="text-[10px] text-gray-500">Facturen uploaden gebeurt via <strong>Inkomende documenten</strong>. Bij koppelen wordt de <strong>factuurdatum</strong> altijd als kostdatum gebruikt.</div>
    </div>
  );
};

export default KostInkomendKoppel;
